import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const defaultConfig = {
  required_rolls: 5,
  pieces_per_roll: 8,
  base_pieces: 40,
  extra_roll_price: 7.8,
  roll_options: [],
  nigiri_options: [],
  sashimi_options: [],
  preorder_days: 1,
};

const optionsText = (options) => (options || []).join("\n");
const parseOptions = (value) => value.split("\n").map((option) => option.trim()).filter(Boolean);

export default function PlatterConfigEditor({ value, onChange }) {
  const config = { ...defaultConfig, ...(value || {}) };
  const update = (key, next) => onChange({ ...config, [key]: next });

  return (
    <section
      className="space-y-3 border border-orange-200 bg-orange-50 p-4"
      data-testid="admin-platter-configuration"
    >
      <div>
        <h3 className="font-display font-semibold">Party platter configuration</h3>
        <p className="mt-1 text-xs text-neutral-600">
          These settings control customer selections and server-side pricing.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ConfigNumber
          label="Included rolls"
          value={config.required_rolls}
          onChange={(next) => update("required_rolls", next)}
          testId="platter-config-required-rolls"
          min="1"
        />
        <ConfigNumber
          label="Pieces per roll"
          value={config.pieces_per_roll}
          onChange={(next) => update("pieces_per_roll", next)}
          testId="platter-config-pieces-per-roll"
          min="1"
        />
        <ConfigNumber
          label="Base pieces"
          value={config.base_pieces}
          onChange={(next) => update("base_pieces", next)}
          testId="platter-config-base-pieces"
          min="1"
        />
        <ConfigNumber
          label="Extra roll price"
          value={config.extra_roll_price}
          step="0.01"
          onChange={(next) => update("extra_roll_price", next)}
          testId="platter-config-extra-price"
        />
      </div>
      <OptionList
        label="Available maki rolls"
        value={optionsText(config.roll_options)}
        onChange={(next) => update("roll_options", next)}
        testId="platter-config-roll-options"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <OptionList
          label="Nigiri choices (leave blank when not included)"
          value={optionsText(config.nigiri_options)}
          onChange={(next) => update("nigiri_options", next)}
          testId="platter-config-nigiri-options"
        />
        <OptionList
          label="Sashimi choices (leave blank when not included)"
          value={optionsText(config.sashimi_options)}
          onChange={(next) => update("sashimi_options", next)}
          testId="platter-config-sashimi-options"
        />
      </div>
    </section>
  );
}

function ConfigNumber({ label, value, onChange, testId, step = "1", min = "0" }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        data-testid={testId}
      />
    </div>
  );
}

function OptionList({ label, value, onChange, testId }) {
  const [rawValue, setRawValue] = useState(value);

  useEffect(() => {
    setRawValue(value);
  }, [value]);

  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        value={rawValue}
        onChange={(event) => setRawValue(event.target.value)}
        onBlur={() => onChange(parseOptions(rawValue))}
        rows={4}
        placeholder="One choice per line"
        className="mt-1 bg-white text-sm"
        data-testid={testId}
      />
    </div>
  );
}